import React, { useContext, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { validateEmail, validatePhone, validateText } from '../../../helpers';
import Context from '../../../utils/context';
import { successToast } from '../../ui_kit/utilities/success_toast';
import ChangePassword from '../change_password';
import styles from '../new_settings.module.css';
import { CaretLeftIcon, displayNameFor, initialsFor } from './icons';
import SettingsShell from './settings_shell';

function firstNameFor(user) {
  if (user?.first_name) {
    return user.first_name;
  }

  return displayNameFor(user).split(' ')[0] || '';
}

function lastNameFor(user) {
  if (user?.last_name) {
    return user.last_name;
  }

  const nameParts = displayNameFor(user).split(' ');
  return nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
}

function phoneFor(user) {
  return user?.phone || '';
}

function usesPasswordlessAuth(user) {
  return (
    user?.passwordless_only === true ||
    (user?.skipped_password === true && user?.is_super_user !== true) ||
    user?.passwordless === true ||
    user?.passwordless_enabled === true ||
    user?.uses_passwordless_auth === true ||
    user?.sign_in_method === 'passwordless' ||
    user?.auth_provider === 'passwordless'
  );
}

function EditableField({ label, value, onChange, type = 'text', autoComplete, placeholder }) {
  return (
    <label className={styles.edit_field}>
      <span>{label}</span>
      <input
        className={styles.edit_input}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        autoComplete={autoComplete}
        placeholder={placeholder}
        translate='no'
      />
    </label>
  );
}

export default function NewProfileEdit() {
  const context = useContext(Context);
  const history = useHistory();
  const user = context.auth.getCurrentUser();
  const userDisplayName = displayNameFor(user);
  const userInitials = initialsFor(userDisplayName);
  const avatarUrl = user?.profile?.photo || user?.current_profile?.photo || '';
  const isMentor = user?.current_profile_type === 'PatientPartner';
  const currentFirstName = firstNameFor(user);
  const currentLastName = lastNameFor(user);
  const currentPhone = phoneFor(user);
  const isPasswordlessUser = usesPasswordlessAuth(user);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [firstName, setFirstName] = useState(currentFirstName);
  const [lastName, setLastName] = useState(currentLastName);
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState(currentPhone);
  const [clickModal, setClickModal] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const identity = useMemo(() => {
    if (isMentor && avatarUrl && !avatarFailed) {
      return <img src={avatarUrl} alt={userDisplayName} draggable='false' onError={() => setAvatarFailed(true)} />;
    }

    return <span>{userInitials || 'PP'}</span>;
  }, [avatarFailed, avatarUrl, isMentor, userDisplayName, userInitials]);

  const trimmedFirstName = firstName.trim();
  const trimmedLastName = lastName.trim();
  const trimmedEmail = email.trim();
  const trimmedPhone = phone.trim();
  const hasChanges =
    trimmedFirstName !== currentFirstName ||
    trimmedLastName !== currentLastName ||
    trimmedEmail !== (user?.email || '') ||
    trimmedPhone !== currentPhone;

  const handleSubmit = (event) => {
    event.preventDefault();
    setError('');

    if (!validateText(trimmedFirstName)) {
      setError('Enter a valid first name.');
      return;
    }

    if (!validateText(trimmedLastName)) {
      setError('Enter a valid last name.');
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError('Enter a valid email.');
      return;
    }

    if (!validatePhone(trimmedPhone)) {
      setError('Enter a valid phone number.');
      return;
    }

    const updates = {};
    if (trimmedFirstName !== currentFirstName) updates.first_name = trimmedFirstName;
    if (trimmedLastName !== currentLastName) updates.last_name = trimmedLastName;
    if (trimmedEmail !== (user?.email || '')) updates.email = trimmedEmail;
    if (trimmedPhone !== currentPhone) updates.phone = trimmedPhone;
    if (Object.keys(updates).length === 0) return;

    setSaving(true);
    context.auth.userUpdate(
      updates,
      (updatedUser) => {
        setSaving(false);
        setFirstName(firstNameFor(updatedUser));
        setLastName(lastNameFor(updatedUser));
        setEmail(updatedUser?.email || '');
        setPhone(phoneFor(updatedUser));
        successToast('Your changes have been saved.');
      },
      (errors) => {
        setSaving(false);
        setError(Array.isArray(errors) ? errors.join(', ') : errors || 'Unable to update profile.');
      }
    );
  };

  return (
    <SettingsShell>
      <div className={styles.edit_page}>
        <div className={`${styles.edit_inner} ${styles.motion_stack}`}>
          <div className={`${styles.edit_header} ${styles.motion_item}`}>
            <button type='button' onClick={() => history.goBack()} aria-label='Back'>
              <CaretLeftIcon className={styles.edit_back_arrow} strokeWidth={2} />
            </button>
            <h1>Edit Profile</h1>
          </div>
          <div className={`${styles.identity_card} ${styles.motion_item}`}>
            <span className={styles.identity_avatar}>{identity}</span>
            <strong translate='no'>{userDisplayName}</strong>
          </div>
          <form className={`${styles.edit_card} ${styles.motion_item}`} onSubmit={handleSubmit}>
            <div className={styles.edit_fields_row}>
              <EditableField label='First name' value={firstName} onChange={setFirstName} autoComplete='given-name' />
              <EditableField label='Last name' value={lastName} onChange={setLastName} autoComplete='family-name' />
            </div>
            <EditableField label='Email' type='email' value={email} onChange={setEmail} autoComplete='email' />
            <EditableField label='Phone number' type='tel' value={phone} onChange={setPhone} autoComplete='tel' />
            <div className={styles.password_section}>
              <p className={styles.password_heading}>Password</p>
              {!isPasswordlessUser ? (
                <button
                  type='button'
                  className={styles.password_link}
                  onClick={() => setClickModal(true)}
                  data-pendo-tag='settings - change password'
                >
                  Change password
                </button>
              ) : (
                <p className={styles.passwordless_note}>
                  Password changes are not available for passwordless sign-in accounts.
                </p>
              )}
            </div>
            {error && <p className={styles.edit_error}>{error}</p>}
            <div className={styles.edit_actions}>
              <button type='submit' className={styles.edit_save} disabled={!hasChanges || saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </form>
          {clickModal && <ChangePassword setClickModal={setClickModal} />}
        </div>
      </div>
    </SettingsShell>
  );
}
